// Local / Cloud tab toggle hosted inside the MINE rail section.
// Mirrors CommunityTabs structurally so the two tabbed surfaces in
// the rail use the same shape.
//
// Both children stay mounted under a CSS `hidden` so a tab switch
// doesn't drop fetched cloud rows or scroll position. The Cloud tab
// runs its own RLS-gated query against Supabase; the Local tab is
// the existing IDB-backed sample list.

import { useState } from 'react';
import { MySamples } from './MySamples';
import { MyCloudSamples } from './MyCloudSamples';
import type { PreviewOps } from './SoundLibrary';

export type MineTabsProps = {
  preview: PreviewOps;
  onOpenForge: () => void;
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
};

type Tab = 'local' | 'cloud';

export function MineTabs({ preview, onOpenForge, onStatus }: MineTabsProps) {
  const [tab, setTab] = useState<Tab>('local');

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex gap-1 px-3 pt-2">
        <TabButton active={tab === 'local'} onClick={() => setTab('local')}>
          Local
        </TabButton>
        <TabButton active={tab === 'cloud'} onClick={() => setTab('cloud')}>
          Cloud
        </TabButton>
      </div>
      <div className="flex-1 min-h-0 relative">
        <div className={tab === 'local' ? 'h-full' : 'hidden'}>
          <MySamples preview={preview} onOpenForge={onOpenForge} />
        </div>
        <div className={tab === 'cloud' ? 'h-full' : 'hidden'}>
          <MyCloudSamples onStatus={onStatus} />
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border-b-2 transition-colors ' +
        (active
          ? 'border-emerald-300 text-emerald-300'
          : 'border-transparent text-zinc-500 hover:text-zinc-300')
      }
    >
      {children}
    </button>
  );
}
